import { computed, toValue } from 'vue';

import {
  str as StrLib,
  SpotType,
  isSpotAssignedToAssetVisit,
  isSpotAssignedToAssetContainer,
  AssetContainerType
} from '@nova/core';
import { useYardSpotGridPresenter } from '@satellite/composables/useYardSpotGridPresenter';
import { toISpot, useYardSpotStatus } from '@satellite/composables/useYardSpotStatus';
import { useYardSpotDisplayStatus } from '@satellite/composables/yard/useYardSpotDisplayStatus';
import { getInboundInProgressAssetContainerId } from '@satellite/composables/yard/spotGridTypes';

import type { ISpot, Maybe } from '@nova/core';
import type { MaybeRefOrGetter, ComputedRef } from 'vue';
import type { SpotGridItem } from '@satellite/composables/useYardSpotGridPresenter';

interface IGroupedSpot {
  code: string;
  id: string;
  icon: string;
  group: string;
  status: string;
  disabled: boolean;
  statusColor: string;
  raw: ISpot;
}

type IGroupedItem = { header: string } | IGroupedSpot;

/**
 * Options for useYardSpotSelect composable.
 *
 * `warehouseId` wants a getter, not a value: a Pinia store is `reactive`, so
 * `store.selectedWarehouseId` reads as a plain string and passing it directly would pin the picker
 * to whatever warehouse was selected when the dialog mounted — and these dialogs never unmount.
 */
interface IUseYardSpotSelectOptions {
  warehouseId?: MaybeRefOrGetter<string | null>;
  includeClosedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssignedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeReservedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssociatedToActiveMoveTasks?: MaybeRefOrGetter<boolean | 'disabled'>;
  disableSpotsWithAssetContainer?: MaybeRefOrGetter<boolean>;
  disableSpotsWithInboundAssetInTransit?: MaybeRefOrGetter<boolean>;
  movingAssetContainerId?: MaybeRefOrGetter<Maybe<string>>;
  assetType?: MaybeRefOrGetter<AssetContainerType | null>;
  search?: MaybeRefOrGetter<string>;
  showDisabled?: MaybeRefOrGetter<boolean>;
}

interface IYardSpotSelect {
  spotsByAreaRef: ComputedRef<IGroupedItem[]>;
  isLoadingRef: ComputedRef<boolean>;
  hasNoSpotsRef: ComputedRef<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Composable for spot selection functionality.
 *
 * Areas, spots, assignments and the open move tasks holding each spot all come down together from
 * the `yard/view/spot-grid` BFF, so the picker never stitches task refs client-side. Filtering,
 * search and grouping stay local because the endpoint returns the whole option set.
 */
export function useYardSpotSelect(options: IUseYardSpotSelectOptions = {}): IYardSpotSelect {
  const {
    warehouseId,
    includeClosedSpots = false,
    includeAssignedSpots = false,
    includeReservedSpots = false,
    includeAssociatedToActiveMoveTasks = false,
    disableSpotsWithAssetContainer = false,
    disableSpotsWithInboundAssetInTransit = false,
    movingAssetContainerId = null,
    assetType = null,
    search = '',
    showDisabled = true
  } = options;

  // `headless`: the dialog is mounted for the whole authenticated session (see `App.vue`), so there
  // is no viewport to gate the fetch on.
  const {
    data: gridAreas,
    isLoading,
    refresh
  } = useYardSpotGridPresenter({
    warehouseId: computed(() => toValue(warehouseId) || null),
    headless: true
  });

  const spotStatus = useYardSpotStatus();
  const { getSpotDisplayStatusKey, getSpotDisplayStatusColor } = useYardSpotDisplayStatus();

  /**
   * Get spot icon based on spot type.
   */
  const getSpotIcon = (type: string): string => {
    return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
  };

  /**
   * Check if asset should be excluded based on filters
   */
  const shouldExcludeClosedSpot = (spot: SpotGridItem): boolean => spotStatus.isSpotClosed(spot);

  const shouldExcludeReservedSpot = (spot: SpotGridItem): boolean =>
    spotStatus.isSpotReserved(spot);

  const shouldExcludeAssignedSpot = (spot: SpotGridItem): boolean =>
    spotStatus.isSpotAssigned(spot);

  /**
   * Whether an open move task holds this spot, as resolved server-side: the task either targets the
   * spot, or moves the container currently parked on it.
   */
  const shouldExcludeSpotAssociatedToActiveMoveTask = (spot: SpotGridItem): boolean =>
    spotStatus.isSpotInActiveMoveTask(spot);

  const isOccupiedByAssetContainer = (spot: SpotGridItem): boolean =>
    isSpotAssignedToAssetContainer(toISpot(spot));

  const hasOtherAssetContainerInTransit = (spot: SpotGridItem): boolean => {
    const inboundAssetContainerId = getInboundInProgressAssetContainerId(spot);

    return (
      Boolean(inboundAssetContainerId) &&
      inboundAssetContainerId !== toValue(movingAssetContainerId)
    );
  };

  /**
   * Check if spot should be disabled based on asset type requirements
   */
  const shouldDisableByAssetType = (spot: SpotGridItem): boolean => {
    const assetTypeValue = toValue(assetType);

    if (!assetTypeValue || spotStatus.isSpotOpen(spot)) {
      return false;
    }

    const spotAsISpot = toISpot(spot);

    if (assetTypeValue === AssetContainerType.Trailer) {
      // For assetVisit: spot must have assetContainer, but not assetVisit
      return (
        !isSpotAssignedToAssetContainer(spotAsISpot) || isSpotAssignedToAssetVisit(spotAsISpot)
      );
    }

    if (assetTypeValue === AssetContainerType.Container) {
      // For assetContainer: spot must not have assetContainer
      return isSpotAssignedToAssetContainer(spotAsISpot);
    }

    return false;
  };

  /**
   * Check if spot should be disabled (included but not selectable)
   */
  const shouldDisableSpot = (spot: SpotGridItem): boolean => {
    const includeClosedValue = toValue(includeClosedSpots);
    const includeAssignedValue = toValue(includeAssignedSpots);
    const includeReservedValue = toValue(includeReservedSpots);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const includeClosedDisabled =
      includeClosedValue === 'disabled' && spotStatus.isSpotClosed(spot);
    const includeAssignedDisabled =
      includeAssignedValue === 'disabled' && spotStatus.isSpotAssigned(spot);
    const includeReservedDisabled =
      includeReservedValue === 'disabled' && spotStatus.isSpotReserved(spot);
    const includeMoveTaskDisabled =
      includeMoveTaskValue === 'disabled' && spotStatus.isSpotInActiveMoveTask(spot);
    const assetContainerDisabled =
      toValue(disableSpotsWithAssetContainer) && isOccupiedByAssetContainer(spot);
    const inboundTransitDisabled =
      toValue(disableSpotsWithInboundAssetInTransit) && hasOtherAssetContainerInTransit(spot);

    return (
      includeClosedDisabled ||
      includeAssignedDisabled ||
      includeReservedDisabled ||
      includeMoveTaskDisabled ||
      assetContainerDisabled ||
      inboundTransitDisabled ||
      shouldDisableByAssetType(spot)
    );
  };

  const groupedSpotsByArea = computed((): IGroupedItem[] => {
    const searchQuery = toValue(search)?.trim().toLowerCase() || '';
    const showDisabledValue = toValue(showDisabled);

    const includeClosedValue = toValue(includeClosedSpots);
    const includeAssignedValue = toValue(includeAssignedSpots);
    const includeReservedValue = toValue(includeReservedSpots);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const exclusions = [
      ...(includeClosedValue ? [] : [shouldExcludeClosedSpot]),
      ...(includeReservedValue ? [] : [shouldExcludeReservedSpot]),
      ...(includeAssignedValue ? [] : [shouldExcludeAssignedSpot]),
      ...(includeMoveTaskValue ? [] : [shouldExcludeSpotAssociatedToActiveMoveTask])
    ];

    const isExcludedBySomeFilter = (spot: SpotGridItem): boolean =>
      exclusions.some(exclude => exclude(spot));

    /**
     * Helper function to check if a string value matches the search query.
     * Handles null/undefined values and performs case-insensitive matching.
     */
    const matchesSearchString = (value: string | null | undefined): boolean => {
      if (!value || !searchQuery) {
        return false;
      }
      return value.toLowerCase().includes(searchQuery);
    };

    const items: IGroupedItem[] = [];

    for (const area of gridAreas.value) {
      const matchesAreaName = matchesSearchString(area.name);
      const matchesSearch = (spot: SpotGridItem): boolean =>
        !searchQuery || matchesSearchString(spot.code) || matchesAreaName;

      const rows: IGroupedSpot[] = (area.spots ?? [])
        .filter(spot => !isExcludedBySomeFilter(spot) && matchesSearch(spot))
        .map(spot => ({
          code: spot.code,
          id: spot.id,
          icon: getSpotIcon(spot.type),
          group: area.name,
          status: getSpotDisplayStatusKey(spot),
          disabled: shouldDisableSpot(spot),
          statusColor: getSpotDisplayStatusColor(spot),
          raw: toISpot(spot)
        }))
        .filter(row => showDisabledValue || !row.disabled)
        .sort((a, b) => StrLib.compareAlphaNumeric(a.code, b.code));

      if (rows.length > 0) {
        items.push({ header: area.name.toUpperCase() });
        items.push(...rows);
      }
    }

    return items;
  });

  const hasNoSpots = computed(() => !isLoading.value && groupedSpotsByArea.value.length === 0);

  return {
    spotsByAreaRef: groupedSpotsByArea,
    isLoadingRef: isLoading,
    hasNoSpotsRef: hasNoSpots,

    refresh
  };
}
